import { useState } from 'react';
import { Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { PageHeader } from '../../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../../shared/grid/usePersistedGridState';
import { IncludeInactiveToggle } from '../../../../shared/components/IncludeInactiveToggle';
import { ConfirmDialog } from '../../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../../shared/components/ErrorState';
import { useToast } from '../../../../shared/stores/toast.store';
import { describeApiError } from '../../../../shared/api/errors';
import { cargosHooks } from '../hooks';
import { cargosApi } from '../api';
import { CargoFormDialog } from '../CargoFormDialog';
import type { CargoDto, CargoListItem } from '../types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'descricao',
  sortOrder: 'asc',
};

export function CargosListPage(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { query, setQuery, clear } = usePersistedGridState('cargos', DEFAULT_QUERY, {});
  const [editing, setEditing] = useState<CargoDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<CargoListItem | null>(null);

  const listQuery = cargosHooks.useList(query);
  const removeMutation = cargosHooks.useDelete();

  async function openEdit(row: CargoListItem): Promise<void> {
    try {
      const detail = await cargosApi.get(row.id);
      setEditing(detail);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await removeMutation.mutateAsync(pendingDelete.id);
      toast.success(t('features.cargos.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<CargoListItem>[] = [
    {
      field: 'descricao',
      headerName: t('features.cargos.columns.descricao'),
      flex: 1,
      minWidth: 220,
    },
    {
      field: 'ativo',
      headerName: t('features.cargos.columns.ativo'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={
            params.value ? t('features.cargos.status.active') : t('features.cargos.status.inactive')
          }
          color={params.value ? 'success' : 'default'}
          size="small"
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'atualizadoEm',
      headerName: t('features.cargos.columns.atualizadoEm'),
      width: 170,
      valueFormatter: (value: string | null) =>
        value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '—',
    },
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.cargos.actions.edit', { descricao: params.row.descricao })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.cargos.actions.delete', { descricao: params.row.descricao })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <>
        <PageHeader title={t('menu.positions')} />
        <ErrorState
          description={describeApiError(listQuery.error)}
          onRetry={() => listQuery.refetch()}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('menu.positions')}
        breadcrumbs={[{ label: t('menu.generalRegistries') }, { label: t('menu.positions') }]}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
            {t('features.cargos.actions.new')}
          </Button>
        }
      />

      <DataTable<CargoListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.cargos.searchPlaceholder')}
        filters={<IncludeInactiveToggle query={query} onChange={setQuery} />}
      />

      <CargoFormDialog
        open={editing !== null}
        cargo={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.cargos.deleteConfirm.title')}
        description={t('features.cargos.deleteConfirm.description', {
          descricao: pendingDelete?.descricao ?? '',
        })}
        confirmLabel={t('common.delete')}
        loading={removeMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
